/**
 * Popup lifecycle management
 * 
 * Handles closing popups via BroadcastChannel and cleanup.
 */

import { isPopupWindow, getPopupIdFromUrl } from './urlUtils';

// BroadcastChannel for popup management
const POPUP_MANAGEMENT_CHANNEL = 'ais-popup-management';

/**
 * BroadcastChannel message types
 */
enum PopupMessageType {
  CLOSE_ALL = 'POPUP_CLOSE_ALL',
  CLOSE_SINGLE = 'POPUP_CLOSE_SINGLE',
}

interface PopupMessage {
  type: PopupMessageType;
  popupId?: string;
}

/**
 * Create or get the popup management channel
 */
let managementChannel: BroadcastChannel | null = null;

const getManagementChannel = (): BroadcastChannel => {
  if (!managementChannel) {
    managementChannel = new BroadcastChannel(POPUP_MANAGEMENT_CHANNEL);
  }
  return managementChannel;
};

/**
 * Close the current popup window
 */
export const closePopup = (): void => {
  window.close();
};

/**
 * Close all popup windows via BroadcastChannel
 * Call this when logging out from the main window
 */
export const closeAllPopups = (): void => {
  const channel = getManagementChannel();
  channel.postMessage({
    type: PopupMessageType.CLOSE_ALL,
  } as PopupMessage);
};

/**
 * Close a specific popup window via BroadcastChannel
 * 
 * @param popupId - The ID of the popup to close
 */
export const closePopupById = (popupId: string): void => {
  const channel = getManagementChannel();
  channel.postMessage({
    type: PopupMessageType.CLOSE_SINGLE,
    popupId,
  } as PopupMessage);
};

/**
 * Setup BroadcastChannel listener for popup management
 * Call this once during app initialization in both main window and popups
 */
export const setupPopupMessageListener = (): void => {
  const channel = getManagementChannel();

  channel.addEventListener('message', (event: MessageEvent<PopupMessage>) => {
    const { type, popupId } = event.data;

    // Only process close commands if this is a popup window
    if (!isPopupWindow()) {
      return;
    }

    switch (type) {
      case PopupMessageType.CLOSE_ALL:
        window.close();
        break;

      case PopupMessageType.CLOSE_SINGLE:
        if (popupId) {
          const currentPopupId = getPopupIdFromUrl();
          if (currentPopupId === popupId) {
            window.close();
          }
        }
        break;
    }
  });
};

/**
 * Cleanup BroadcastChannel on window unload
 */
export const cleanupPopupChannel = (): void => {
  if (managementChannel) {
    managementChannel.close();
    managementChannel = null;
  }
};

/**
 * Initialize popup lifecycle management
 * 
 * - Main window: broadcasts CLOSE_ALL when unloaded
 * - Popup windows: listen for close commands and credential removal
 */
export const initializePopupLifecycle = (): void => {
  if (!isPopupWindow()) {
    // Main window: ensure popups are told to close when the main window unloads
    window.addEventListener('unload', () => {
      try {
        // Signal to popups that main window is closing
        try {
          localStorage.setItem('ais_popup_close', Date.now().toString());
        } catch {
          // Ignore localStorage errors
        }
        // Broadcast the close command to any open popups
        closeAllPopups();
      } catch (err) {
        console.error('Error broadcasting popup close on unload', err);
      } finally {
        // Clean up the management channel
        cleanupPopupChannel();
      }
    });
  } else {
    // Popup window: listen for logout via storage (credentials removed) and close
    window.addEventListener('storage', (event: StorageEvent) => {
      if (
        (event.key === 'credentials' && event.newValue === null) ||
        event.key === 'ais_popup_close'
      ) {
        window.close();
      }
    });

    // Ensure the BroadcastChannel listener is active in popups
    setupPopupMessageListener();

    // Clean up channel on unload
    window.addEventListener('unload', () => {
      cleanupPopupChannel();
    });
  }
};

// Auto-initialize lifecycle management when this module is imported
initializePopupLifecycle();
